import { useState, useEffect, useCallback } from "react";
import {
  NotesService,
  NoteResponseDto,
  CreateNoteDto,
  UpdateNoteDto,
} from "@/services/NotesService";
import {
  BackendFilter,
  FilterSettingDto,
  FilterOperation,
  FilterType,
} from "@/types/filter";

export const useNotes = (memberId: string) => {
  const [notes, setNotes] = useState<NoteResponseDto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const notesService = new NotesService(memberId);

  // Fetch all notes with optional filtering
  const fetchNotes = useCallback(
    async (filter?: {
      type?: "personal" | "family" | "all";
      search?: string;
      page?: number;
      pageSize?: number;
    }) => {
      setLoading(true);
      setError(null);
      try {
        // Compose FilterSettingDto for backend
        const getAllFilter: FilterSettingDto = {
          filters: {
            memberId: {
              value: memberId,
              type: FilterType.STRING,
              operation: FilterOperation.EQUAL,
            },
          },
          sort: [{ field: "createdAt", order: "desc" }],
          pagination: { page: 1, pageSize: 10 },
        };
        if (filter?.type && filter.type !== "all") {
          getAllFilter.filters = {
            ...getAllFilter.filters,
            type: {
              value: filter.type,
              type: FilterType.STRING,
              operation: FilterOperation.EQUAL,
            },
          };
        }
        if (filter?.search) {
          getAllFilter.filters = {
            ...getAllFilter.filters,
            title: {
              value: filter.search,
              type: FilterType.STRING,
              operation: FilterOperation.CONTAINS,
            },
          };
        }
        if (filter?.page) getAllFilter.pagination.page = filter.page;
        if (filter?.pageSize)
          getAllFilter.pagination.pageSize = filter.pageSize;
        const data = await notesService.getNotes(getAllFilter);
        setNotes(data.data);
        return data.data;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to fetch notes";
        setError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [memberId]
  );

  // Get a single note by ID
  const fetchNote = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const note = await notesService.getNote(id);
      return note;
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : `Failed to fetch note with ID: ${id}`;
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new note
  const createNote = useCallback(async (noteData: CreateNoteDto) => {
    setLoading(true);
    setError(null);
    try {
      const newNote = await notesService.createNote(noteData);
      setNotes((prevNotes) => [...prevNotes, newNote]);
      return newNote;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to create note";
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update an existing note
  const updateNote = useCallback(
    async (id: string, noteData: UpdateNoteDto) => {
      setLoading(true);
      setError(null);
      try {
        const updatedNote = await notesService.updateNote(id, noteData);
        setNotes((prevNotes) =>
          prevNotes.map((note) => (note.id === id ? updatedNote : note))
        );
        return updatedNote;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : `Failed to update note with ID: ${id}`;
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Delete a note
  const deleteNote = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      await notesService.deleteNote(id);
      setNotes((prevNotes) => prevNotes.filter((note) => note.id !== id));
      return true;
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : `Failed to delete note with ID: ${id}`;
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Upload attachment for a note
  const uploadAttachment = useCallback(async (file: File) => {
    setLoading(true);
    setError(null);
    try {
      const media = await notesService.uploadAttachment(file);
      return media;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to upload attachment";
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Add a comment to a note
  const addComment = useCallback(async (noteId: string, content: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await notesService.addComment(noteId, content);
      return result;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to add comment";
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update a comment on a note
  const updateComment = useCallback(
    async (noteId: string, commentId: string, content: string) => {
      setLoading(true);
      setError(null);
      try {
        const result = await notesService.updateComment(
          noteId,
          commentId,
          content
        );
        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to update comment";
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Delete a comment on a note
  const deleteComment = useCallback(
    async (noteId: string, commentId: string) => {
      setLoading(true);
      setError(null);
      try {
        const result = await notesService.deleteComment(noteId, commentId);
        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to delete comment";
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Mark a note as read
  const markAsReadNote = useCallback(async (noteId: string) => {
    setLoading(true);
    setError(null);
    try {
      return await notesService.markAsReadNote(noteId);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to mark note as read";
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch unread family notes count
  const fetchFamilyUnreadCount = useCallback(async () => {
    try {
      const count = await notesService.getFamilyUnreadCount();
      return count || 0;
    } catch {
      return 0;
    }
  }, [memberId, notesService]);

  return {
    notes,
    loading,
    error,
    fetchNotes,
    fetchNote,
    createNote,
    updateNote,
    deleteNote,
    uploadAttachment,
    addComment,
    updateComment,
    deleteComment,
    markAsReadNote,
    fetchFamilyUnreadCount,
  };
};

export default useNotes;
